import React from "react"
import { useCookies } from "react-cookie"
import Head from "next/head"
import { GetStaticPaths, GetStaticProps, NextPage } from "next"
import * as Sentry from "@sentry/nextjs"
import { Container, Row, Col } from "react-bootstrap"
import Header from "@modules/Common/components/Header"
import { Page } from "@styled/dashboard"
import ActivityList from "@modules/Activity/components/ActivityList"
import SkillHeader from "@modules/Skill/components/SkillHeader"
import { ActivitiesGrid } from "@modules/Activity/styled/Activity"
import { initializeApollo } from "apollo/client"
import PublicActivityList from "@modules/Activity/components/PublicActivityList"
import { PublicSkillDocument, PublicSkillQuery, PublicSkillQueryVariables } from "@generated/graphql"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"

interface ISkillActivities {
  slug: string
  skill: PublicSkillQuery["publicSkill"]
  error: boolean
}

const SkillActivitiesPage: NextPage<ISkillActivities> = ({ skill, slug }) => {
  const [cookies] = useCookies(["idToken"])

  const title = `${skill?.metaTagData?.title || skill?.name || ""} | MySkillShaala`
  const description = skill?.metaTagData?.description || skill?.description || ""
  const image = skill?.metaTagData?.image || skill?.image || DEFAULT_META_IMAGE_URL

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/skills/${skill?.slug}`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/skills/${skill?.slug}`} />
        <meta property="twitter:title" content={`${title}`} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
        {skill && <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Skills",
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/skills`
            }, {
              "@type": "ListItem",
              "position": 2,
              "name": skill.name,
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/skills/${skill.slug}`
            }]
          })
        }} />}
      </Head>
      <Page>
        <Header />
        <Container fluid>
          <SkillHeader slug={slug} skill={skill} />
          {cookies.idToken ? <>
            <Row className="mt-3">
              <Col lg={{ span: 10, offset: 1 }} className="p-3">
                <ActivitiesGrid>
                  <ActivityList where={{ skill: { slug: slug } }} />
                </ActivitiesGrid>
              </Col>
            </Row>
          </> : <>
            <Row className="mt-3">
              <Col lg={{ span: 10, offset: 1 }} className="p-3">
                <ActivitiesGrid>
                  <PublicActivityList where={{ skill: { slug: slug } }} />
                </ActivitiesGrid>
              </Col>
            </Row>
          </>}
        </Container>
      </Page>
    </>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return { paths: [], fallback: "blocking" }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug[0] : params?.slug!!
  const apolloClient = initializeApollo()
  try {
    const { data, error } = await apolloClient.query<PublicSkillQuery, PublicSkillQueryVariables>({
      query: PublicSkillDocument,
      variables: {
        where: {
          slug: slug
        }
      }
    })
    if (data) {
      return {
        props: {
          skill: data.publicSkill,
          slug: slug,
          initialApolloState: apolloClient.cache.extract(),
        },
        revalidate: 600
      }
    } else {
      Sentry.captureException(error)
      return {
        props: {
          slug: slug,
          error: true
        }
      }
    }
  } catch (error: any) {
    return {
      props: {
        slug: slug,
        error: true
      }
    }
  }
}

export default SkillActivitiesPage
